import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { languageOptions } from "./languages";
import "./LanguageSwitcher.css";

export function LanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const disclosure = useRef<HTMLDetailsElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const activeLanguage = languageOptions.find((option) => option.code === i18n.language);

  const changeLanguage = (language: string) => {
    const search = new URLSearchParams(location.search);
    if (language === "en") search.delete("lang");
    else search.set("lang", language);
    if (disclosure.current) {
      disclosure.current.open = false;
      disclosure.current.querySelector("summary")?.focus();
    }
    void i18n.changeLanguage(language);
    navigate({
      pathname: location.pathname,
      search: search.toString() ? `?${search.toString()}` : "",
      hash: location.hash,
    }, { replace: true, state: location.state });
  };

  return (
    <details className="langs" ref={disclosure} onBlur={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node)) event.currentTarget.open = false;
    }} onKeyDown={(event) => {
      if (event.key === "Escape") {
        event.currentTarget.open = false;
        event.currentTarget.querySelector("summary")?.focus();
      }
    }}>
      <summary aria-label={`${t("language.switch")}: ${activeLanguage?.nativeName}`}>
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
          <circle cx="12" cy="12" r="9" /><ellipse cx="12" cy="12" rx="4" ry="9" /><path d="M3 12h18" />
        </svg>
        {activeLanguage?.shortName}
        <svg className="lang-chevron" viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="m4 6 4 4 4-4" /></svg>
      </summary>
      <div className="lang-options" role="group" aria-label={t("language.switch")}>
      {languageOptions.map((option) => (
        <button
          key={option.code}
          type="button"
          lang={option.code}
          aria-label={option.nativeName}
          aria-pressed={i18n.language === option.code}
          onClick={() => changeLanguage(option.code)}
        >
          <span className="lang-full" aria-hidden="true">{option.nativeName}</span>
          {i18n.language === option.code && <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="m3 8 3 3 7-7" /></svg>}
        </button>
      ))}
      </div>
    </details>
  );
}
